import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import type { ApiClient, Geraetezeile, Produktdaten, Produktkreis,
              Produktpunkt } from '../api';
import { Schema } from './Schemata';

/**
 * Die Produktseite eines Geraets.
 *
 * --- Warum eigene Stile statt Klassen aus styles.css --------------------------
 *
 * Die Seite laeuft in ZWEI Rahmen: innerhalb der Anwendung mit Kopfzeile und
 * Reitern, und oeffentlich ueber einen signierten Verweis aus Revit - dort ohne
 * beides. Waere sie auf die Anwendungsstile angewiesen, muesste der oeffentliche
 * Rahmen sie mitbringen und beide auseinanderlaufen.
 *
 * Deshalb traegt sie ihre Darstellung selbst. Nur `karte` kommt von aussen -
 * die Umrandung soll sich anpassen, wenn die Anwendung sie aendert.
 *
 * --- Warum ein Schema und keine Tabelle ---------------------------------------
 *
 * Eine Kaeltemaschine IST ein hydraulisches Schema: zwei Kreise, vier
 * Anschluesse, eine Fliessrichtung. Darin erkennt ein Planer eine verwechselte
 * Vor- und Ruecklaufseite sofort. In einer Tabelle mit den Spalten Eintritt und
 * Austritt nicht.
 */

// --- Farben ------------------------------------------------------------------
//
// Als rgba-Auflagen und nicht als Volltoene: So tragen sie auf hellem wie auf
// dunklem Grund, und die Seite haengt nicht daran, welches Thema die Anwendung
// gerade fuehrt.
const F = {
  warm: '#e8845f',
  kalt: '#5fb8e0',
  linie: 'rgba(128,150,170,0.28)',
  schwach: 'rgba(154,184,208,0.85)',
  sehrSchwach: 'rgba(154,184,208,0.55)',
  gruen: '#7ec98f',
  gelb: '#e8c96a',
  blau: '#7fb4e8',
  grau: '#8b98a5',
};

/** Die vier Herkunftsfarben, wie sie das Backend liefert. */
const HERKUNFT: Record<string, { farbe: string; text: string }> = {
  gruen: { farbe: F.gruen, text: 'aus dem Modell gelesen, dorthin schreibbar' },
  gelb: { farbe: F.gelb, text: 'nur in der Datenbank gefuehrt' },
  blau: { farbe: F.blau, text: 'von der Plattform gerechnet' },
  grau: { farbe: F.grau, text: 'als falsch erkannt und entwertet' },
};

const zahl = (n: number | null | undefined, stellen = 0) =>
  n === null || n === undefined
    ? '–'
    : n.toLocaleString('de-DE', {
        minimumFractionDigits: stellen, maximumFractionDigits: stellen });

const kW = (w: number | null | undefined) =>
  w === null || w === undefined ? '–' : `${zahl(w / 1000, 0)} kW`;

// ═══ Die Geräteliste ═════════════════════════════════════════════════════════

/**
 * Alle Geraete eines Projekts.
 *
 * Gegliedert nach Bauteilart, weil 754 Geraete in einer Liste niemand liest.
 * Die Zahl der Hydraulikkreise sagt, wo die Datenpflege schon steht: ein Geraet
 * ohne Kreise hat keine Betriebspunkte und damit keine Auslegung.
 */
export function Geraeteliste({ api }: { api: ApiClient }) {
  const { id: projektId } = useParams<{ id: string }>();
  const [zeilen, setZeilen] = useState<Geraetezeile[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [suche, setSuche] = useState('');
  const [art, setArt] = useState('');
  const [meldung, setMeldung] = useState<string | null>(null);

  useEffect(() => {
    if (!projektId) return;
    api.geraete(projektId).then(setZeilen)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId]);

  const arten = useMemo(() => {
    const m = new Map<string, number>();
    for (const z of zeilen ?? []) m.set(z.bauteilart, (m.get(z.bauteilart) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [zeilen]);

  const gefiltert = useMemo(() => {
    const t = suche.trim().toLowerCase();
    return (zeilen ?? []).filter((z) =>
      (!art || z.bauteilart === art)
      && (!t || [z.modell, z.hersteller, z.raumnummer, z.raumname, z.typ,
                 String(z.revitElementId)]
            .some((x) => (x ?? '').toLowerCase().includes(t))));
  }, [zeilen, suche, art]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!zeilen) return <div className="leer">wird geladen …</div>;

  return (
    <div className="karte">
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <input placeholder="Modell, Raum, ElementId …" value={suche}
          onChange={(e) => setSuche(e.target.value)} style={{ minWidth: 220 }} />
        <select value={art} onChange={(e) => setArt(e.target.value)}>
          <option value="">alle Bauteilarten ({zeilen.length})</option>
          {arten.map(([a, n]) => <option key={a} value={a}>{a} ({n})</option>)}
        </select>
        <button onClick={async () => {
          if (!projektId) return;
          setMeldung('Verweise werden gebildet …');
          try {
            const e = await api.geraeteVerweise(projektId);
            setMeldung(`${e.geraete} Gerät(e): ${e.neu} neu, ${e.geaendert} `
                     + `geändert, ${e.unveraendert} unverändert. ${e.hinweis}`);
          } catch (x) {
            setMeldung(x instanceof Error ? x.message : String(x));
          }
        }} title="Bildet für jedes Gerät den öffentlichen Verweis und trägt ihn
 als Bauteilwert ein. Beim nächsten Rückschreiben wandert er ins Modell.">
          Verweise eintragen
        </button>

        <span style={{ marginLeft: 'auto', fontSize: 12, color: F.schwach }}>
          {gefiltert.length} von {zeilen.length}
        </span>
      </div>

      {meldung && (
        <div className="hinweis" style={{ marginBottom: 12 }}>{meldung}</div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ color: F.schwach, fontSize: 11, textAlign: 'left' }}>
            <th style={{ padding: '6px 8px' }}>Art</th>
            <th style={{ padding: '6px 8px' }}>Fabrikat und Typ</th>
            <th style={{ padding: '6px 8px' }}>Raum</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Werte</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Kreise</th>
            <th style={{ padding: '6px 8px' }} />
          </tr>
        </thead>
        <tbody>
          {gefiltert.slice(0, 300).map((z) => (
            <tr key={z.id} style={{ borderTop: `1px solid ${F.linie}` }}>
              <td style={{ padding: '7px 8px', color: F.schwach }}>{z.bauteilart}</td>
              <td style={{ padding: '7px 8px' }}>
                {z.hersteller || z.modell
                  ? <strong>{[z.hersteller, z.modell].filter(Boolean).join(' ')}</strong>
                  : <span style={{ color: F.sehrSchwach }}>
                      {z.familie} · {z.typ}
                    </span>}
              </td>
              <td style={{ padding: '7px 8px', color: F.schwach }}>
                {z.raumnummer ?? '–'}
              </td>
              <td style={{ padding: '7px 8px', textAlign: 'right' }}>{z.werte}</td>
              <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                {z.kreise
                  ? <span style={{ color: F.gruen }}>{z.kreise}</span>
                  : <span style={{ color: F.sehrSchwach }}>–</span>}
              </td>
              <td style={{ padding: '7px 8px' }}>
                <Link to={`../geraete/${z.id}`} className="knopf">Details</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {gefiltert.length > 300 && (
        <p style={{ fontSize: 12, color: F.schwach }}>
          Nur die ersten 300 gezeigt. Über die Suche einschränken.
        </p>
      )}
    </div>
  );
}

// ═══ Die Detailansicht in der Anwendung ══════════════════════════════════════

export function ProduktseiteIntern({ api }: { api: ApiClient }) {
  const { bauteilId } = useParams<{ bauteilId: string }>();
  const [daten, setDaten] = useState<Produktdaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!bauteilId) return;
    api.geraet(bauteilId).then(setDaten)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, bauteilId]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!daten) return <div className="leer">wird geladen …</div>;

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Link to="../geraete" className="knopf">← Geräte</Link>
      </div>
      <Produktseite d={daten} intern />
    </>
  );
}

/**
 * Dieselbe Seite fuer eine Lueftungskomponente, innerhalb der Anwendung.
 *
 * Der einzige Unterschied zur Geraeteseite ist der Endpunkt und der Rueckweg.
 * Die Darstellung ist dieselbe - und das ist der Zweck: Wer eine
 * Volumenstromklappe ansieht, soll dieselben vier Herkunftsfarben lesen wie
 * bei einer Kaeltemaschine.
 */
export function KomponenteIntern({ api }: { api: ApiClient }) {
  const { komponenteId } = useParams<{ komponenteId: string }>();
  const [daten, setDaten] = useState<Produktdaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!komponenteId) return;
    api.lueftungKomponente(komponenteId).then(setDaten)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, komponenteId]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!daten) return <div className="leer">wird geladen …</div>;

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Link to="../lueftung" className="knopf">← Lüftung</Link>
      </div>
      <Produktseite d={daten} intern />
    </>
  );
}

// ═══ Der öffentliche Rahmen ══════════════════════════════════════════════════

/**
 * Die Seite hinter dem signierten Verweis aus Revit.
 *
 * Ohne MSAL: Der Abruf geht mit einfachem fetch an den offenen Endpunkt, und die
 * Signatur wandert als Abfrageteil mit. Ein Monteur mit dem QR-Code aus dem Plan
 * soll das Bauteil sehen und keine Anmeldung.
 *
 * Eigener Kopf, keine Navigation: Die Anwendungsnavigation waere hier nicht
 * bedienbar - jeder Verweis darin fuehrte auf die Anmeldung.
 */
/**
 * Dieselbe Seite fuer eine Lueftungskomponente.
 *
 * Eine eigene Route und ein eigener Endpunkt, aber DIESELBE Darstellung: Die
 * Antwort hat die gleiche Form, und zwei Seiten fuer dasselbe liefen
 * auseinander, sobald jemand eine davon aendert.
 */
export function KomponenteOeffentlich() {
  return <OeffentlichRahmen pfad="lueftung-komponente" />;
}

export function ProduktseiteOeffentlich() {
  return <OeffentlichRahmen pfad="bauteil" />;
}

/**
 * Dieselbe Seite fuer ein SANITAERGERAET, innerhalb der Anwendung.
 *
 * Der einzige Unterschied ist der Endpunkt. Die Darstellung ist dieselbe - und
 * das ist der Zweck der Vereinheitlichung: Wer einen Durchlauferhitzer ansieht,
 * soll dieselben Herkunftsfarben lesen wie bei einer Kaeltemaschine, und
 * dasselbe Schema sehen.
 *
 * Moeglich ist das, weil der Sanitaerendpunkt dieselbe ANTWORTFORM liefert -
 * nicht, weil die Anzeige Fallunterscheidungen trifft.
 */
export function SanitaerGeraetIntern({ api }: { api: ApiClient }) {
  const { bauteilId } = useParams<{ bauteilId: string }>();
  const [daten, setDaten] = useState<Produktdaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!bauteilId) return;
    api.sanitaerGeraet(bauteilId).then(setDaten)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, bauteilId]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!daten) return <div className="leer">wird geladen …</div>;

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Link to="../sanitaer" className="knopf">← Sanitär</Link>
      </div>
      <Produktseite d={daten} intern />
    </>
  );
}

/** Und fuer ein ELEKTROBAUTEIL. */
export function ElektroGeraetIntern({ api }: { api: ApiClient }) {
  const { bauteilId } = useParams<{ bauteilId: string }>();
  const [daten, setDaten] = useState<Produktdaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!bauteilId) return;
    api.elektroGeraet(bauteilId).then(setDaten)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, bauteilId]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!daten) return <div className="leer">wird geladen …</div>;

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Link to="../elektro-geraete" className="knopf">← Elektrogeräte</Link>
      </div>
      <Produktseite d={daten} intern />
    </>
  );
}

/**
 * Und für ein SPRINKLERGERÄT.
 *
 * Dieselbe Seite wie bei der Heizung — nicht eine, die so aussieht. Der
 * Endpunkt liefert die Form, die dieser Baustein liest; ein nachgebautes
 * Aussehen mit eigener Datenform wäre zweimal dasselbe und liefe beim ersten
 * Umbau des Bausteins auseinander.
 */
export function SprinklerGeraetIntern({ api }: { api: ApiClient }) {
  const { bauteilId } = useParams<{ bauteilId: string }>();
  const [daten, setDaten] = useState<Produktdaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!bauteilId) return;
    api.sprinklerGeraet(bauteilId).then(setDaten)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, bauteilId]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!daten) return <div className="leer">wird geladen …</div>;

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Link to="../sprinkler" className="knopf">← Sprinkler</Link>
      </div>
      <Produktseite d={daten} intern />
    </>
  );
}

/** Und für ein AUSSENANLAGENBAUTEIL. */
export function AussenGeraetIntern({ api }: { api: ApiClient }) {
  const { bauteilId } = useParams<{ bauteilId: string }>();
  const [daten, setDaten] = useState<Produktdaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!bauteilId) return;
    api.aussenGeraet(bauteilId).then(setDaten)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, bauteilId]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!daten) return <div className="leer">wird geladen …</div>;

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Link to="../aussenanlagen" className="knopf">← Außenanlagen</Link>
      </div>
      <Produktseite d={daten} intern />
    </>
  );
}

/** Die oeffentlichen Wege - je Gewerk ein eigener Pfad. */
export function SanitaerseiteOeffentlich() {
  return <OeffentlichRahmen pfad="sanitaer" />;
}

export function ElektroseiteOeffentlich() {
  return <OeffentlichRahmen pfad="elektro" />;
}

export function SprinklerseiteOeffentlich() {
  return <OeffentlichRahmen pfad="sprinklergeraet" />;
}

export function AussenseiteOeffentlich() {
  return <OeffentlichRahmen pfad="aussengeraet" />;
}

function OeffentlichRahmen({ pfad }: { pfad: string }) {
  // Der Routenparameter heisst bei beiden Wegen bauteilId - die Route
  // bestimmt, welche Tabelle dahinter steht, nicht der Name.
  const { bauteilId } = useParams<{ bauteilId: string }>();
  const signatur = new URLSearchParams(useLocation().search).get('s') ?? '';

  const [daten, setDaten] = useState<Produktdaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!bauteilId) return;

    fetch(`/api/oeffentlich/${pfad}/${bauteilId}`
        + `?s=${encodeURIComponent(signatur)}`)
      .then((r) => {
        // 404 auch bei falscher Signatur - der Endpunkt sagt absichtlich nicht,
        // ob die Kennung existiert. Der Text hier muss beides abdecken.
        if (!r.ok) throw new Error(
          'Dieser Verweis ist nicht gültig. Er wurde entweder verkürzt kopiert '
          + 'oder das Bauteil ist nicht mehr im Bestand.');
        return r.json();
      })
      .then(setDaten)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [bauteilId, signatur, pfad]);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '18px 20px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10,
                    paddingBottom: 12, marginBottom: 20,
                    borderBottom: `1px solid ${F.linie}` }}>
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.05em',
                       color: F.schwach }}>TEUBER+VIEL GmbH</span>
        <span style={{ fontSize: 11, color: F.sehrSchwach }}>
          Technische Bauteildaten
        </span>
      </div>

      {fehler && <div className="hinweis fehler">{fehler}</div>}
      {!fehler && !daten && <div className="leer">wird geladen …</div>}
      {daten && <Produktseite d={daten} intern={false} />}

      <p style={{ marginTop: 34, fontSize: 11, color: F.sehrSchwach }}>
        Diese Angaben stammen aus der Fachplanung und dienen der Information.
        Verbindlich ist die Ausschreibung.
      </p>
    </div>
  );
}

// ═══ Die Darstellung selbst ══════════════════════════════════════════════════

/**
 * Das ANLAGENKENNZEICHEN im Kopf — oder der Grund, warum keines dasteht
 * (Paket S8, F39/F54).
 *
 * --- Warum im Kopf und nicht als Feldzeile ------------------------------------
 *
 * Das AKS steht als Feld `anlagenkennzeichen` auch unter „Kennzeichnung",
 * zwischen achtzig anderen. Wer vor dem Gerät steht und das Schild sucht,
 * liest den Kopf. Dieselbe Entscheidung wie bei Raum und Geschoss.
 *
 * --- Warum ein Grund und keine leere Zeile ------------------------------------
 *
 * Fehlt das AKS, hat das zwei ganz verschiedene Bedeutungen: Ein
 * PAM-GLOBAL-Doppelabzweig bekommt nach F39 NIE eines (er ist ein Formteil,
 * kein Gerät) — ein Durchlauferhitzer ohne AKS wäre eine Lücke. Ohne den
 * Grund sehen beide gleich aus, und die erste Sorte erzeugt eine
 * Arbeitsliste, die es nicht gibt.
 *
 * Fehlen die Felder ganz (alle Gewerke außer Sanitär liefern sie heute
 * nicht), zeigt die Leiste NICHTS — statt „kein AKS" zu behaupten.
 */
function Aksleiste({ d }: { d: Produktdaten }) {
  if (!d.aksEinstufung) return null;

  const luecke = d.aksEinstufung === 'fehlt';

  return (
    <div style={{ marginTop: 8, fontSize: 13 }}>
      <span style={{ fontSize: 11, color: F.schwach, letterSpacing: '.05em',
                     textTransform: 'uppercase', marginRight: 8 }}>
        AKS
      </span>

      {d.aksSchluessel
        ? (
          <span style={{ fontFamily: 'monospace', fontSize: 14 }}>
            {d.aksSchluessel}
          </span>
        )
        : (
          <span style={{ color: luecke ? F.gelb : F.schwach }}>
            {luecke ? 'fehlt' : 'keine'}
          </span>
        )}

      {/* Der Grund IMMER, auch bei vergebener Adresse: Er sagt, nach welcher
          Festlegung sie dasteht. */}
      <div style={{ fontSize: 11.5, color: F.schwach, marginTop: 4,
                    lineHeight: 1.5, maxWidth: 520 }}>
        {d.aksGrund}
      </div>
    </div>
  );
}

function Produktseite({ d, intern }: { d: Produktdaten; intern: boolean }) {
  // Alle Betriebsarten, die an irgendeinem Kreis vorkommen. Der
  // Auslegungspunkt zuerst - er ist der, nach dem dimensioniert wird.
  const modi = useMemo(() => {
    const m = new Map<string, { modus: string; text: string; ausl: boolean }>();
    for (const k of d.kreise) {
      for (const p of k.betriebspunkte) {
        const vorhanden = m.get(p.modus);
        m.set(p.modus, {
          modus: p.modus,
          text: p.modusDe ?? p.modus,
          ausl: (vorhanden?.ausl ?? false) || p.istAuslegung,
        });
      }
    }
    return [...m.values()].sort(
      (a, b) => Number(b.ausl) - Number(a.ausl) || a.modus.localeCompare(b.modus));
  }, [d]);

  const [modus, setModus] = useState(modi[0]?.modus ?? '');

  useEffect(() => { if (modi.length && !modus) setModus(modi[0].modus); },
    [modi, modus]);

  const punkt = (k: Produktkreis) =>
    k.betriebspunkte.find((p) => p.modus === modus);

  return (
    <>
      {/* --- Kopf --- */}
      <div className="karte" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start',
                      flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px' }}>
            <div style={{ fontSize: 11, letterSpacing: '.05em',
                          color: F.schwach, textTransform: 'uppercase' }}>
              {d.bauteilbezeichnung ?? d.bauteilart}
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 300, margin: '4px 0 6px' }}>
              {[d.hersteller, d.modell].filter(Boolean).join(' ') || d.typ}
            </h1>
            <div style={{ fontSize: 13, color: F.schwach }}>
              {d.raumnummer && <>Raum {d.raumnummer}</>}
              {d.raumname && <> · {d.raumname}</>}
              {d.geschossgruppe && <> · {d.geschossgruppe}</>}
            </div>

            <Aksleiste d={d} />
          </div>

          <Kennwerte d={d} intern={intern} />
        </div>

        {intern && d.datenstandGrund && (
          <p style={{ marginBottom: 0, marginTop: 14, fontSize: 12,
                      color: F.schwach }}>{d.datenstandGrund}</p>
        )}
      </div>

      {/* --- Betriebsart --- */}
      {modi.length > 1 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center',
                      flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: F.schwach, marginRight: 4 }}>
            Betriebsart
          </span>
          {modi.map((m) => (
            <button key={m.modus}
              className={m.modus === modus ? 'betont' : undefined}
              onClick={() => setModus(m.modus)}
              title={m.ausl
                ? 'Nach diesem Punkt wird dimensioniert'
                : 'Weiterer Betriebspunkt'}>
              {m.text}{m.ausl ? ' ★' : ''}
            </button>
          ))}
          <span style={{ fontSize: 11, color: F.sehrSchwach, marginLeft: 6 }}>
            ★ Auslegungspunkt
          </span>
        </div>
      )}

      {/* --- Das Herstellerkennfeld ------------------------------------
          Nur bei Lüftungskomponenten. Die Stützpunkte UND der abgeleitete
          Punkt zusammen: Wer eine hochgerechnete Zahl sieht, will die
          Grundlage daneben haben - sonst ist sie eine Behauptung mit
          Nachkommastelle. */}
      {d.betriebspunkt && (
        <div className="karte" style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            Betriebspunkt aus dem Herstellerkennfeld
          </div>

          {d.betriebspunkt.herkunft === 'keine' ? (
            <p style={{ fontSize: 12, color: F.schwach, margin: 0 }}>
              {d.betriebspunkt.hinweis}
            </p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap',
                            marginBottom: 10 }}>
                {[
                  ['Volumenstrom', d.betriebspunkt.volumenstromM3h, 'm³/h', 0],
                  ['Druckverlust', d.betriebspunkt.druckverlustPa, 'Pa', 1],
                  ['Schallleistung', d.betriebspunkt.lwaDb, 'dB(A)', 1],
                ].map(([bez, wert, einheit, stellen]) => (
                  <div key={bez as string}>
                    <div style={{ fontSize: 11, color: F.schwach }}>{bez}</div>
                    <div style={{ fontSize: 17 }}>
                      {zahl(wert as number | null, stellen as number)}{' '}
                      <span style={{ fontSize: 12, color: F.schwach }}>
                        {einheit}
                      </span>
                    </div>
                  </div>
                ))}

                {d.betriebspunkt.volumenstromHerkunft === 'kanalnetz' && (
                  <div>
                    <div style={{ fontSize: 11, color: F.schwach }}>
                      Volumenstrom aus
                    </div>
                    <div style={{ fontSize: 13, marginTop: 3 }}>
                      angeschlossenem Kanal
                    </div>
                  </div>
                )}

                <div>
                  <div style={{ fontSize: 11, color: F.schwach }}>Herkunft</div>
                  <div style={{ fontSize: 13, marginTop: 3 }}>
                    <span style={{
                      display: 'inline-block', width: 9, height: 9,
                      borderRadius: 2, marginRight: 6,
                      background: d.betriebspunkt.istGerechnet ? F.blau : F.gelb,
                    }} />
                    {d.betriebspunkt.istGerechnet
                      ? 'gerechnet' : 'Datenblattwert'}
                  </div>
                </div>
              </div>

              {/* Der Weg zum Wert. Immer, auch beim Datenblattwert - eine
                  abgeleitete Zahl ohne Begründung kann niemand verantworten. */}
              <p style={{ fontSize: 11.5, color: F.schwach, margin: 0,
                          lineHeight: 1.5 }}>
                {d.betriebspunkt.hinweis}
              </p>
            </>
          )}

          {d.kennfeld && d.kennfeld.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse',
                            fontSize: 12, marginTop: 14 }}>
              <thead>
                <tr style={{ color: F.schwach, textAlign: 'right' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>
                    Stützpunkte der Baureihe
                  </th>
                  <th style={{ padding: '5px 8px' }}>m³/h</th>
                  <th style={{ padding: '5px 8px' }}>Pa</th>
                  <th style={{ padding: '5px 8px' }}>dB(A)</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>
                    Quelle
                  </th>
                </tr>
              </thead>
              <tbody>
                {d.kennfeld.map((s, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${F.linie}`,
                                       textAlign: 'right' }}>
                    <td style={{ padding: '6px 8px', textAlign: 'left' }}>
                      Größe {s.groesse ?? '–'}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {/* Wo der Hersteller Zeta gibt, steht dort kein
                          Betriebspunkt - dann die Aerodynamik zeigen. */}
                      {s.zeta != null
                        ? `A ${zahl(s.freieFlaecheM2, 3)} m²`
                        : zahl(s.volumenstromM3h)}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {s.zeta != null
                        ? `ζ ${zahl(s.zeta, 2)}`
                        : zahl(s.druckverlustPa)}
                    </td>
                    <td style={{ padding: '6px 8px' }}>{zahl(s.lwaDb)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'left',
                                 color: F.schwach }}>
                      {s.quelle ?? s.herkunft}
                      {!s.geprueft && (
                        <span style={{ marginLeft: 6, color: F.gelb }}>
                          ungeprüft
                        </span>
                      )}
                      {!s.hatOktavband && (
                        <span style={{ marginLeft: 6 }}>
                          ohne Oktavband
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Auf betriebspunkte prüfen und nicht nur auf das Objekt.
          Ein leeres Array ist in JavaScript wahr - der Abschnitt lief an und
          stürzte eine Zeile später ab. Die Seite läuft auch öffentlich, und
          dort heißt ein Absturz: weiße Seite für einen Monteur mit QR-Code. */}
      {d.lueftungsregister?.betriebspunkte && (
        <div className="karte" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, margin: '0 0 4px' }}>
            Lüftungsregister · {d.lueftungsregister.kennzeichnung ?? d.lueftungsregister.anlage}
          </h3>
          <div style={{ fontSize: 12, color: F.schwach, marginBottom: 10 }}>
            {d.lueftungsregister.standort || 'ohne Standort'} · Rohrnetz{' '}
            {d.lueftungsregister.gruppe ?? 'nicht bestimmt'} ·{' '}
            {d.lueftungsregister.einheit ?? d.lueftungsregister.einheitArt ?? 'Register nicht bestimmt'}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ color: F.schwach, fontSize: 11, textAlign: 'right' }}>
                <th style={{ padding: '5px 8px', textAlign: 'left' }}>Betriebspunkt</th>
                <th style={{ padding: '5px 8px' }}>Leistung</th>
                <th style={{ padding: '5px 8px' }}>sensibel / latent</th>
                <th style={{ padding: '5px 8px' }}>Vorlauf / Rücklauf</th>
                <th style={{ padding: '5px 8px' }}>Massenstrom</th>
              </tr>
            </thead>
            <tbody>
              {d.lueftungsregister.betriebspunkte.map((p) => {
                const w: Record<string, string> = p.dataJson ? JSON.parse(p.dataJson) : {};
                return (
                  <tr key={p.id} style={{ borderTop: `1px solid ${F.linie}`, textAlign: 'right' }}>
                    <td style={{ padding: '7px 8px', textAlign: 'left' }}>{p.modusDe ?? p.modus}</td>
                    <td style={{ padding: '7px 8px', fontWeight: 600 }}>{w.bp_leistung ? `${w.bp_leistung} kW` : '–'}</td>
                    <td style={{ padding: '7px 8px' }}>{w.bp_leistung_sens || w.bp_leistung_lat
                      ? `${w.bp_leistung_sens ?? '–'} / ${w.bp_leistung_lat ?? '–'} kW` : '–'}</td>
                    <td style={{ padding: '7px 8px' }}>{w.bp_medium_vl && w.bp_medium_rl
                      ? `${w.bp_medium_vl} / ${w.bp_medium_rl} °C` : '–'}</td>
                    <td style={{ padding: '7px 8px' }}>{w.bp_massenstrom
                      ? `${Number(w.bp_massenstrom).toLocaleString('de-DE', { maximumFractionDigits: 0 })} kg/h` : '–'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* --- Das Schema --- */}
      {d.kreise.length > 0 && (
        <div className="karte" style={{ marginBottom: 16, overflowX: 'auto' }}>
          <Schema d={d} modus={modus} />
        </div>
      )}

      {/* --- Die Kreise mit ihren Betriebspunkten --- */}
      {d.kreise.map((k) => (
        <Kreiskarte key={k.id} k={k} aktiv={punkt(k)} intern={intern} />
      ))}

      {/* --- Die Werte --- */}
      {d.gruppen.map((g) => (
        <div className="karte" key={g.gruppe} style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: 13, letterSpacing: '.04em', margin: '0 0 10px',
                       textTransform: 'uppercase', color: F.schwach }}>
            {g.gruppe}
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse',
                          fontSize: 13 }}>
            <tbody>
              {g.felder.map((f) => (
                <tr key={f.feldschluessel}
                    style={{ borderTop: `1px solid ${F.linie}` }}>
                  <td style={{ padding: '6px 8px 6px 0', width: 18 }}>
                    <span title={HERKUNFT[f.farbe]?.text ?? f.farbe}
                      style={{ display: 'inline-block', width: 8, height: 8,
                               borderRadius: 2,
                               background: HERKUNFT[f.farbe]?.farbe ?? F.grau }} />
                  </td>
                  <td style={{ padding: '6px 8px', color: F.schwach }}>
                    {f.bezeichnung}
                  </td>
                  <td style={{ padding: '6px 8px', fontWeight: 500 }}>
                    {f.farbe === 'grau'
                      ? <span style={{ color: F.grau,
                                       textDecoration: 'line-through' }}>
                          {f.wert}
                        </span>
                      : f.wert}
                    {f.einheit && f.farbe !== 'grau' && (
                      <span style={{ color: F.sehrSchwach }}> {f.einheit}</span>
                    )}
                  </td>
                  {intern && (
                    <td style={{ padding: '6px 8px', fontSize: 11,
                                 color: f.abweichend ? F.warm : F.sehrSchwach,
                                 textAlign: 'right' }}>
                      {f.abweichend
                        ? `im Modell: ${f.imModell}`
                        : f.herkunft}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* --- Legende und Datenblatt --- */}
      <div className="karte" style={{ display: 'flex', gap: 20,
                                      flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap',
                      fontSize: 11, color: F.schwach }}>
          {Object.entries(HERKUNFT).map(([k, v]) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center',
                                   gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2,
                             background: v.farbe }} />
              {v.text}
            </span>
          ))}
        </div>

        {d.datenblattUrl && (
          <a href={d.datenblattUrl} target="_blank" rel="noreferrer"
             className="knopf" style={{ marginLeft: 'auto' }}>
            Datenblatt öffnen
          </a>
        )}
        {d.herstellerUrl && (
          <a href={d.herstellerUrl} target="_blank" rel="noreferrer"
             className="knopf">Herstellerseite</a>
        )}
      </div>

      {intern && d.verweis && <Verweiszeile verweis={d.verweis} />}
    </>
  );
}

// --- Kennwerte im Kopf -------------------------------------------------------

function Kennwerte({ d, intern }: { d: Produktdaten; intern: boolean }) {
  const zellen: [string, string][] = [];

  if (d.kostengruppe) zellen.push(['Kostengruppe', d.kostengruppe]);
  if (d.kreis) zellen.push(['Netzkreis', d.kreis]);
  zellen.push(['ElementId', String(d.revitElementId ?? '–')]);
  if (intern && d.datenstand) zellen.push(['Datenstand', d.datenstand]);

  return (
    <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
      {zellen.map(([k, v]) => (
        <div key={k}>
          <div style={{ fontSize: 10, letterSpacing: '.04em', color: F.sehrSchwach,
                        textTransform: 'uppercase' }}>{k}</div>
          <div style={{ fontSize: 14 }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

// --- Eine Kreiskarte mit allen Betriebspunkten -------------------------------

function Kreiskarte({ k, aktiv, intern }: {
  k: Produktkreis; aktiv?: Produktpunkt; intern: boolean;
}) {
  return (
    <div className="karte" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline',
                    flexWrap: 'wrap', marginBottom: 12 }}>
        <h3 style={{ fontSize: 14, margin: 0 }}>
          {k.bezeichnung ?? k.kreisart}
        </h3>
        <span style={{ fontSize: 12, color: F.schwach }}>
          {k.netzkreis}
          {k.medium && <> · {k.medium}</>}
          {k.glykolProzent ? <> · {zahl(k.glykolProzent)} % Glykol</> : null}
        </span>

        {!k.amNetz && (
          <span title="Die Familie hat keine definierten Anschluesse - das Gerät
 haengt im Modell an keiner Leitung."
                style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3,
                         color: F.warm,
                         border: `1px solid ${F.warm}`, opacity: 0.85 }}>
            nicht am Netz
          </span>
        )}

        {/* Die Stoffwerte gehoeren sichtbar: cp 3700 statt 4190 sind 13 Prozent
            Massenstrom, und die Viskositaet treibt den Druckverlust. */}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: F.sehrSchwach }}>
          cp {zahl(k.waermekapazitaetWsKgK)} Ws/(kg·K) ·
          ρ {zahl(k.dichteKgM3)} kg/m³ ·
          ν {zahl(k.viskositaetMm2S, 2)} mm²/s
          {k.stoffwertBei !== null && k.stoffwertBei !== undefined
            && <> bei {zahl(k.stoffwertBei)} °C</>}
        </span>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ color: F.schwach, fontSize: 11, textAlign: 'right' }}>
            <th style={{ padding: '5px 8px', textAlign: 'left' }}>Betriebspunkt</th>
            <th style={{ padding: '5px 8px' }}>Leistung</th>
            <th style={{ padding: '5px 8px' }}>Ein / Aus</th>
            <th style={{ padding: '5px 8px' }}>ΔT</th>
            <th style={{ padding: '5px 8px' }}>Massenstrom</th>
            <th style={{ padding: '5px 8px' }}>Volumenstrom</th>
            <th style={{ padding: '5px 8px' }}>Δp</th>
            {intern && <th style={{ padding: '5px 8px' }}>Herkunft</th>}
          </tr>
        </thead>
        <tbody>
          {k.betriebspunkte.map((p) => {
            const gewaehlt = aktiv?.id === p.id;
            return (
              <tr key={p.id} style={{
                    borderTop: `1px solid ${F.linie}`, textAlign: 'right',
                    background: gewaehlt
                      ? 'rgba(127,180,232,0.08)' : undefined }}>
                <td style={{ padding: '7px 8px', textAlign: 'left' }}>
                  {p.istAuslegung && (
                    <span title="Nach diesem Punkt wird dimensioniert"
                          style={{ color: F.gruen, marginRight: 5 }}>★</span>
                  )}
                  {p.modusDe ?? p.modus}
                </td>
                <td style={{ padding: '7px 8px', fontWeight: gewaehlt ? 600 : 400 }}>
                  {kW(p.leistungW)}
                </td>
                <td style={{ padding: '7px 8px' }}>
                  {p.eintrittC === null || p.austrittC === null
                    ? '–'
                    : `${zahl(p.eintrittC)} / ${zahl(p.austrittC)} °C`}
                </td>
                <td style={{ padding: '7px 8px' }}>
                  {p.spreizung === null || p.spreizung === undefined
                    ? '–' : `${zahl(p.spreizung, 1)} K`}
                </td>
                <td style={{ padding: '7px 8px' }}>
                  {zahl(p.massenstromKgH)} {p.massenstromKgH ? 'kg/h' : ''}
                </td>
                <td style={{ padding: '7px 8px', fontWeight: gewaehlt ? 600 : 400 }}>
                  {zahl(p.volumenstromM3H, 1)} {p.volumenstromM3H ? 'm³/h' : ''}
                </td>
                <td style={{ padding: '7px 8px',
                             color: p.druckverlustPa ? undefined : F.warm }}>
                  {p.druckverlustPa ? `${zahl(p.druckverlustPa)} Pa` : 'fehlt'}
                </td>
                {intern && (
                  <td style={{ padding: '7px 8px', fontSize: 11,
                               color: F.sehrSchwach }}>
                    {p.herkunft}
                    {p.stimmig === false && (
                      <span style={{ color: F.warm }} title="Leistung, Spreizung
 und Massenstrom passen nicht zusammen."> · unstimmig</span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Der Vergleich der Volumenstroeme als Balken.
          Zwei Punkte sind noch keine Kurve - aber der GROESSENUNTERSCHIED ist
          die Aussage: 310 gegen 171 m³/h ist der Unterschied zwischen richtiger
          und halber Nennweite. */}
      <Balken punkte={k.betriebspunkte} />

      {intern && aktiv?.hinweis && (
        <p style={{ fontSize: 12, color: F.schwach, marginBottom: 0,
                    marginTop: 12 }}>{aktiv.hinweis}</p>
      )}
    </div>
  );
}

function Balken({ punkte }: { punkte: Produktpunkt[] }) {
  const mit = punkte.filter((p) => (p.volumenstromM3H ?? 0) > 0);
  if (mit.length < 2) return null;

  const max = Math.max(...mit.map((p) => p.volumenstromM3H ?? 0));

  return (
    <div style={{ marginTop: 14 }}>
      {mit.map((p) => (
        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                                 marginTop: 5, fontSize: 11 }}>
          <span style={{ width: 150, color: F.schwach, textAlign: 'right' }}>
            {p.modusDe ?? p.modus}
          </span>
          <span style={{ flex: 1, height: 10, background: F.linie,
                         borderRadius: 2, overflow: 'hidden' }}>
            <span style={{
              display: 'block', height: '100%',
              width: `${((p.volumenstromM3H ?? 0) / max) * 100}%`,
              background: p.istAuslegung ? F.gruen : F.blau,
              opacity: p.istAuslegung ? 1 : 0.55 }} />
          </span>
          <span style={{ width: 92, color: F.schwach }}>
            {zahl(p.volumenstromM3H, 1)} m³/h
          </span>
        </div>
      ))}
    </div>
  );
}

// --- Der Verweis zum Kopieren ------------------------------------------------

function Verweiszeile({ verweis }: { verweis: string }) {
  const [kopiert, setKopiert] = useState(false);

  return (
    <div className="karte" style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11, color: F.schwach, marginBottom: 6 }}>
        Öffentlicher Verweis — ohne Anmeldung erreichbar, gehört ins Modell und
        ins IFC
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                    flexWrap: 'wrap' }}>
        <code style={{ flex: '1 1 380px', fontSize: 11, wordBreak: 'break-all',
                       color: F.schwach }}>{verweis}</code>
        <button onClick={() => {
          navigator.clipboard?.writeText(verweis);
          setKopiert(true);
          window.setTimeout(() => setKopiert(false), 2000);
        }}>{kopiert ? 'kopiert' : 'Kopieren'}</button>
        <a href={verweis} target="_blank" rel="noreferrer" className="knopf">
          Öffnen
        </a>
      </div>
    </div>
  );
}
